/**
 * Síťový protokol. Jediný zdroj pravdy pro klienta i Worker — server si ho
 * importuje z tohoto souboru, takže se obě strany nemohou rozejít.
 *
 * Formát je JSON s krátkými klíči. Binární kódování by ušetřilo zhruba
 * polovinu bajtů, ale JSON jde odladit v DevTools a na desítky hráčů v zóně
 * je úspora bezvýznamná proti tomu, co stojí čitelnost.
 */

export const PROTOCOL_VERSION = 1;

/** Jak často klient posílá svůj stav. 12 Hz stačí, zbytek dopočítá interpolace. */
export const STATE_SEND_HZ = 12;

/**
 * O kolik zpátky se vykreslují ostatní hráči. Musí být o kus víc než
 * rozestup snímků stavu, aby bylo vždy z čeho interpolovat i při zakolísání
 * sítě. 1000/12 ≈ 83 ms, takže 140 ms dává rozumnou rezervu.
 */
export const INTERPOLATION_DELAY_MS = 140;

/** Po jak dlouhém tichu se spojení považuje za mrtvé. */
export const HEARTBEAT_INTERVAL_MS = 15_000;
export const CONNECTION_TIMEOUT_MS = 40_000;

/**
 * Perioda společných hodin světa v sekundách.
 *
 * Vlny, mraky i vítr se počítají z času. Kdyby si každý klient počítal svůj
 * vlastní od nuly, viděli by dva hráči stojící vedle sebe jinou vodu.
 * Proto se čas odvozuje od serverového a bere se zbytek po dělení touto
 * periodou — bez toho by číslo za pár dní narostlo tak, že by ve float32
 * v shaderu ztratilo přesnost a hladina by začala cukat.
 *
 * Cena je jeden skok fáze za 24 hodin. Trvá jeden snímek a nikdo si ho
 * nevšimne.
 */
export const WORLD_CLOCK_PERIOD_S = 86_400;

/**
 * Konstanty světa sdílené s Workerem.
 *
 * Bydlí tady, a ne v `config.ts`, protože ten sahá na `import.meta.env`,
 * které ve Workeru neexistuje. `config.ts` je odsud přebírá, takže zdroj
 * pravdy zůstává jeden.
 */
export const WORLD_SEED = 1337;
export const WORLD_SIZE = 2048;
export const SEA_LEVEL = 12;

/** Nejvyšší rychlost, jakou hráč dokáže vyvinout (sprint). */
export const MAX_GROUND_SPEED = 9.5;
/** Počáteční rychlost skoku — z ní plyne, jak vysoko se lze dostat. */
export const MAX_JUMP_VELOCITY = 8.5;

/**
 * Tolerance pro kontrolu rychlosti na serveru.
 *
 * Musí být štědrá: zprávy chodí po nespolehlivé síti, můžou se shluknout
 * po výpadku a klient běží na proměnlivém framerate. Cílem není chytit
 * hráče o deset procent rychlejšího, ale toho, kdo se teleportuje.
 */
export const SPEED_TOLERANCE = 2.5;

/** Jak hluboko pod terén a jak vysoko nad něj se hráč smí dostat. */
export const MAX_BELOW_GROUND = 3;
export const MAX_ABOVE_GROUND = 30;

/** Strop na počet zpráv za sekundu od jednoho klienta. */
export const MAX_MESSAGES_PER_SECOND = 40;

export const MAX_NAME_LENGTH = 16;
export const MAX_CHAT_LENGTH = 200;
export const MAX_PLAYERS_PER_ZONE = 40;

// --- souboj -------------------------------------------------------------

export const PLAYER_MAX_HP = 100;
export const SPIDER_MAX_HP = 40;
/** Kolik ubere jeden zásah mečem. */
export const SWORD_DAMAGE = 20;
/** Kolik ubere kousnutí pavouka. */
export const SPIDER_DAMAGE = 8;
/** Jak dlouho trvá, než se hráč po smrti vrátí do hry. */
export const PLAYER_RESPAWN_SECONDS = 4;
/** Jak dlouho trvá, než se pavouk objeví znovu. */
export const SPIDER_RESPAWN_SECONDS = 12;
/** Kolikrát za sekundu posílá simulující hráč stav nepřátel. */
export const NPC_SEND_HZ = 8;

/** Animační stavy pavouka. Kód se posílá po síti. */
export const SPIDER_ANIMS = ['idle', 'walk', 'attack', 'death'] as const;
export type SpiderAnim = (typeof SPIDER_ANIMS)[number];

export const SPIDER_CLIPS: Record<SpiderAnim, string> = {
  idle: 'Spider_Idle',
  walk: 'Spider_Walk',
  attack: 'Spider_Attack',
  death: 'Spider_Death',
};

/** Stav lokomoce, zkrácený na jeden bajt kvůli objemu zpráv. */
export const ANIM_CODES = [
  'idle',
  'walk',
  'sprint',
  'jump_start',
  'jump_loop',
  'jump_land',
  'dance',
  'swim_idle',
  'swim_fwd',
  // Nové stavy se přidávají vždy na konec. Klient se starším protokolem
  // dostane neznámý kód a codeToAnim mu vrátí 'idle' — uvidí stát, ne spadne.
  'fish_idle',
  'fish_cast',
  'fish_bite',
  'fish_reel',
  'fish_catch',
  'attack',
  'dead',
] as const;

export type AnimState = (typeof ANIM_CODES)[number];

export function animToCode(state: string): number {
  const index = (ANIM_CODES as readonly string[]).indexOf(state);
  return index < 0 ? 0 : index;
}

export function codeToAnim(code: number): AnimState {
  return ANIM_CODES[code] ?? 'idle';
}

/** Vzhled postavy tak, jak putuje po síti. Volný typ, aby server nemusel
 *  znát detaily tvorby postavy — jen je přeposílá. */
export interface NetAppearance {
  gender: string;
  hairstyle: string;
  beard: string;
  eyebrows: string;
  skinTone: string;
  hairColor: string;
  outfit: string;
  hood: boolean;
}

export interface NetPlayer {
  id: string;
  name: string;
  /** Aktuální životy. Nově příchozí tak nevidí mrtvého hráče v plné síle. */
  hp?: number;
  appearance: NetAppearance;
  /** Kde má nahozeno, pokud rybaří. Aby ho nově příchozí viděl rybařit. */
  spot?: [number, number] | null;
  /** Pozice [x, y, z]. */
  p: [number, number, number];
  /** Natočení kolem svislé osy v radiánech. */
  r: number;
  /** Kód animačního stavu. */
  a: number;
}

// --- klient -> server ---------------------------------------------------

export type ClientMessage =
  | {
      t: 'join';
      v: number;
      name: string;
      appearance: NetAppearance;
      /** Trvalé id z localStorage — podle něj server pozná vracejícího se hráče. */
      pid: string;
    }
  | { t: 'st'; p: [number, number, number]; r: number; a: number }
  | { t: 'look'; appearance: NetAppearance }
  | { t: 'chat'; m: string }
  /**
   * Místo nahození, nebo null při ukončení rybaření.
   *
   * Skákající ryby se nepřenáší — každý klient si je spočítá deterministicky
   * z těchto souřadnic a společného času světa. Po síti jde jedna zpráva
   * za nahození, ne desítky ryb za sekundu.
   */
  | { t: 'spot'; p: [number, number] | null }
  /**
   * Stav nepřátel od hráče, který je zrovna simuluje.
   *
   * Formát je pole čísel místo objektů — při osmi aktualizacích za sekundu
   * a hrsti pavouků je rozdíl v objemu znatelný a zpráva se stejně nikde
   * neukládá ani nečte lidským okem.
   */
  | { t: 'npc'; s: NpcTuple[] }
  /** Zásah nepřítele. Uplatní ho jen ten, kdo nepřátele simuluje. */
  | { t: 'hit'; id: number; dmg: number }
  /** Zásah hráče. Uplatní si ho sám cíl. */
  | { t: 'pdmg'; id: string; dmg: number }
  /** Vlastní životy se změnily. */
  | { t: 'hp'; hp: number }
  | { t: 'ping'; n: number };

/** `[id, x, y, z, yaw, animKod, hp]` */
export type NpcTuple = [number, number, number, number, number, number, number];

// --- server -> klient ---------------------------------------------------

export type ServerMessage =
  | {
      t: 'welcome';
      id: string;
      players: NetPlayer[];
      serverTime: number;
      /** Uložená pozice z minula, pokud server hráče poznal. */
      resume?: { p: [number, number, number]; r: number };
    }
  | { t: 'spawn'; player: NetPlayer }
  | { t: 'leave'; id: string }
  | { t: 'st'; id: string; p: [number, number, number]; r: number; a: number }
  | { t: 'look'; id: string; appearance: NetAppearance }
  | { t: 'chat'; id: string; name: string; m: string }
  | { t: 'spot'; id: string; p: [number, number] | null }
  | { t: 'npc'; from: string; s: NpcTuple[] }
  | { t: 'hit'; id: number; dmg: number; by: string }
  | { t: 'pdmg'; id: string; dmg: number; by: string }
  | { t: 'hp'; id: string; hp: number }
  | { t: 'pong'; n: number; serverTime: number }
  | { t: 'error'; code: string; message: string }
  /** Server odmítl pozici a vrací hráče na poslední platnou. */
  | { t: 'correct'; p: [number, number, number]; reason: string };

/**
 * Ořízne jméno na povolenou délku a vyhodí řídicí znaky.
 *
 * Běží na serveru i na klientovi. Klientská kontrola je jen pohodlí —
 * autoritativní je ta serverová, protože zprávy může poslat kdokoliv.
 */
function stripControlChars(value: string): string {
  let out = "";
  for (const ch of value) {
    const code = ch.codePointAt(0) ?? 0;
    if (code >= 32 && code !== 127) out += ch;
  }
  return out;
}

export function sanitizeName(raw: string): string {
  return stripControlChars(raw).replace(/ {2,}/g, " ").trim().slice(0, MAX_NAME_LENGTH);
}

export function sanitizeChat(raw: string): string {
  return stripControlChars(raw).trim().slice(0, MAX_CHAT_LENGTH);
}
